// AdAccountList.tsx - Material React Table 버전
import { useEffect, useState } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { AdAccountType } from './useLoadAccount';
import AdAccountTable_SelectBox from './AdAccountTable_SelectBox';
import { statusType } from 'src/pages/AdminPage/AdAccountPage';
import { useAdAccountStore } from 'src/stores/AdAccountStore';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import styled from 'styled-components';

const TableHeaderSx = {
  backgroundColor: '#30515B',
  color: 'white',
  fontSize: '14px',
  textAlign: 'center',
  '& .Mui-TableHeadCell-Content': {
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    height: '100%',
  },
  '&:first-of-type': {
    borderTopLeftRadius: '5px',
    borderBottomLeftRadius: '5px',
  },
  '&:last-of-type': {
    borderTopRightRadius: '5px',
    borderBottomRightRadius: '5px',
  },
}

const TableRowSx = {
  height: '49px',
  '& > td:first-of-type': {
    borderTopLeftRadius: '5px',
    borderBottomLeftRadius: '5px',
  },
  '& > td:last-of-type': {
    borderTopRightRadius: '5px',
    borderBottomRightRadius: '5px',
  },
  '& > td': {
    background: '#FFF',
    boxShadow: '1px 2px 2px rgba(0, 0, 0, 0.1)',
    padding: '4px',
  },
}

const TableCellSx = {
  textAlign: 'center',
  verticalAlign: 'middle',
  fontSize: '16px',
}

const EmptyDiv = styled.div`
  text-align: center;
  padding: 20px;
  font-size: 20px;
  font-weight: 600;
`

export const getStatusFromRow = (row: any): string => {
  const { is_active, admin_approval } = row;
  if (!is_active && !admin_approval) return '대기';
  if (!is_active && admin_approval) return '차단';
  if (is_active && admin_approval) return '승인';
  return ''; // 기타 상태는 필터링 제외
};

interface Props{
  propsData: AdAccountType[]
  status : statusType
}

const AdAccountTable = ({propsData, status}:Props) => {
  const filteredData = propsData.filter((row) => {
    const rowStatus = getStatusFromRow(row);
    if (!status || status === '전체') return ['대기', '차단', '승인'].includes(rowStatus); // 전체면 모두 표시
    return rowStatus === status;
  });
  // useEffect(()=>{
  //   filteredData.forEach((row) => {
  //   const resolvedStatus = getStatusFromRow(row);
  // });
  // },[status])
  const page = useAdAccountStore((state)=>state.page)
  const totalpage = useAdAccountStore((state)=>state.totalpage)
  const setField = useAdAccountStore((state)=>state.setField)

  const [pagination, setPagination] = useState({
    pageIndex: page-1,
    pageSize: 1,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: page - 1, // 상태는 0-based
    }));
  }, [page]);

  const handlePageChange = (page: number) => {
    
    setPagination((prev) => ({ ...prev, pageIndex: page }));
    setField('page',page+1); // 부모에게 알림
  };
  
  return (
    <div style={{height:'100%'}}>
    <MaterialReactTable
      data={filteredData}
      muiTableContainerProps={{
        sx: { 
          height: 'calc(100vh - 450px)',
          overflowY: 'auto',
          overflowX: 'auto',
          width: 'calc(100vw - 425px)'
        }
      }}
      columns={[
        { accessorKey: 'user_name', header: '이름' },
        { accessorKey: 'user_id', header: '아이디' },
        // { accessorKey: 'army_number', header: '군번' },
        { accessorKey: 'phone', header: '연락처' },
        { accessorKey: 'email', header: '이메일' },
        {
          header: '상태',
          accessorFn: (row) => getStatusFromRow(row),
          // 내부 셀 셀렉트박스
          Cell: ({ cell }) => <AdAccountTable_SelectBox cell={cell}/>
        }
      ]}
      // 툴바, sort 버튼 등
      enableTopToolbar={false}
      enableBottomToolbar={false}
      enableColumnActions={false}
      enableSorting={false}
      muiTablePaperProps={{
        elevation: 0, // ✅ 그림자 제거
      }}
      // 로우 헤더 스타일
      muiTableHeadCellProps={{
        sx: TableHeaderSx,
      }}
      // 로우 스타일
      muiTableBodyRowProps={{
      sx: TableRowSx,
      }}
      // 셀 스타일
      muiTableBodyCellProps={{
        sx: TableCellSx,
      }}
      // 테이블 스타일
      muiTableProps={{
        sx: {
          borderCollapse: 'separate',
          borderSpacing: '0 5px', // ✅ row 간격 5px
      }}}
      // 페이지 네이션 옵션
      state={{ pagination }}
      manualPagination 
      pageCount={totalpage} // 수동 계산 필요 시
      renderEmptyRowsFallback={() => (
      <EmptyDiv>
        {status === '대기' && '대기 중인 계정이 없습니다'}
        {status === '승인' && '승인 대기 중인 계정이 없습니다'}
        {status === '전체' && '관리 가능한 계정이 없습니다'}
        {status === '차단' && '차단된 계정이 없습니다'}
      </EmptyDiv>
    )}
    />
    <Box display="flex" justifyContent="center" mt={2} gap={1}>
            {/* 이전 버튼 */}
            <IconButton
              onClick={() => handlePageChange(Math.max(pagination.pageIndex - 1, 0))}
              disabled={pagination.pageIndex === 0}
              aria-label="이전 페이지"
            >
              <ChevronLeft />
            </IconButton>
    
            {/* 숫자 버튼 */}
            {Array.from({ length: totalpage }).map((_, index) => (
              <IconButton
                key={index}
                onClick={() => handlePageChange(index)}
                sx={{
                  backgroundColor:
                    pagination.pageIndex === index ? '#50818b' : 'transparent',
                  color: pagination.pageIndex === index ? '#fff' : '#666',
                  '&:hover': {
                    backgroundColor:
                      pagination.pageIndex === index ? '#4a747d' : '#f0f0f0',
                  },
                  width: 32,
                  height: 32,
                  fontSize: 14,
                }}
              >
                {index + 1}
              </IconButton>
            ))}
    
            {/* 다음 버튼 */}
            <IconButton
              onClick={() =>
                handlePageChange(Math.min(pagination.pageIndex + 1, totalpage - 1))
              }
              disabled={pagination.pageIndex === totalpage - 1}
              aria-label="다음 페이지"
            >
              <ChevronRight />
            </IconButton>
          </Box>
    </div>
  );
};

export default AdAccountTable;
